import type { Chart, ChartData, ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import commaNumber from 'comma-number';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  CHART_CALCULATION,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC_TYPE,
} from '@/entities/chart/model/constants';
import useEventPlugin from '@/features/(charts)/chart-renderer/hooks/use-chart-event-plugin';
import { ChartMetricTypeMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import {
  BaseBarChart,
  useBarChartShowMore,
  useChartTooltip,
} from '@/shared/ui/charts';

import ChartCustomTooltip from '../chart-custom-tooltip/ChartCustomTooltip';
import useChartAccData from './useChartAccData';

// 비-대시보드 수평 bar chart 스크롤 관련 상수
const DESIRED_BAR_HEIGHT = 28;
const MAX_CHART_HEIGHT = 15000;
const MAX_LABEL_LENGTH = 35; // y축 label 최대 글자 수

type Props = {
  chart: ChartBase & { name: string };
  tableData: ChartTableRes;
  dashboardCardInfo?: DashboardCardInfoT;
  yAxisTitle?: string;
};

const BarChart = (props: Props) => {
  const chartRef = useRef<Chart<'bar'>>(null);
  const { accChartData, labels } = useChartAccData(props);
  const { tooltipData, chartTooltip, hideTooltip } = useChartTooltip({
    chart: chartRef.current,
  });

  const eventPlugin = useEventPlugin({
    meta: props.chart,
    hideTooltip,
    dashboardCardInfo: props.dashboardCardInfo,
  });

  const isAvgScoreType =
    ChartMetricTypeMap.get(props.chart.series[0]?.measure.metric) ===
      CHART_METRIC_TYPE.SCORE &&
    props.chart.series[0]?.measure.calculation === CHART_CALCULATION.AVERAGE;

  const isVertical =
    props.chart.graph_direction === CHART_GRAPH_DIRECTION.VERTICAL;
  const isDashboard = !!props.dashboardCardInfo;
  const labelCount = labels?.length ?? 0;

  // 대시보드 수평 bar chart show more 로직
  const {
    containerRef,
    expanded,
    setExpanded,
    hasMore,
    visibleCount,
    totalCount,
    expandedChartHeight,
    collapsedChartHeight,
    isHorizontal,
    chartAreaLeft,
  } = useBarChartShowMore({
    labels,
    isVertical,
    isDashboard,
    chartRef,
  });

  // 비-대시보드: Chart.js 렌더링 후 실제 bar 픽셀 높이를 측정해 스크롤 높이 결정
  const [scrollHeight, setScrollHeight] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    // 대시보드 수평 bar chart: show more/less가 scroll을 CSS로 직접 관리
    if (isDashboard && isHorizontal) return;

    if (isVertical || labelCount === 0) {
      setScrollHeight((prev) => (prev === undefined ? prev : undefined));
      return;
    }

    let retryCount = 0;
    let rafId = 0;

    const measure = () => {
      const chart = chartRef.current;
      const yScale = chart?.scales?.['y'];

      if (!chart || !yScale || !yScale.height) {
        if (retryCount < 10) {
          retryCount += 1;
          rafId = requestAnimationFrame(measure);
        }
        return;
      }

      const totalItems = labelCount;

      // 실제 bar 높이가 DESIRED_BAR_HEIGHT 미만이면 스크롤 활성화
      const perBarHeight = yScale.height / totalItems;
      const chartOverhead = Math.max(chart.height - yScale.height, 0);

      let nextHeight: number | undefined;
      if (perBarHeight < DESIRED_BAR_HEIGHT) {
        const scrollNeeded = Math.ceil(
          totalItems * DESIRED_BAR_HEIGHT + chartOverhead,
        );
        nextHeight = Math.min(scrollNeeded, MAX_CHART_HEIGHT);
      }

      setScrollHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    };

    rafId = requestAnimationFrame(measure);

    return () => cancelAnimationFrame(rafId);
    // scrollHeight는 의도적으로 deps 제외 — 포함 시 무한 루프 발생
     
  }, [isVertical, labelCount, isDashboard, isHorizontal]);

  // 데이터 슬라이싱: collapsed 모드에서 표시할 데이터만 필터링
  const visibleAccChartData = useMemo(() => {
    if (!hasMore || expanded) return accChartData;
    return {
      ...accChartData,
      labels: accChartData.labels?.slice(0, visibleCount),
      datasets: accChartData.datasets.map((ds) => ({
        ...ds,
        data: ds.data.slice(0, visibleCount),
        backgroundColor: Array.isArray(ds.backgroundColor)
          ? ds.backgroundColor.slice(0, visibleCount)
          : ds.backgroundColor,
        // @ts-expect-error: meta is a custom field on ChartDataset
        meta: Array.isArray(ds.meta) ? ds.meta.slice(0, visibleCount) : ds.meta,
      })),
    };
  }, [accChartData, hasMore, expanded, visibleCount]);

  // y축 label 콜백용 visible labels
  const visibleLabels = useMemo(() => {
    if (!hasMore || expanded) return labels;
    return labels?.slice(0, visibleCount);
  }, [labels, hasMore, expanded, visibleCount]);

  const barChartOptions: ChartOptions<'bar'> = useMemo(() => {
    return {
      indexAxis: isVertical ? 'x' : 'y',
      scales: {
        [isVertical ? 'y' : 'x']: {
          grace: '15%',
          grid: {
            color: 'transparent',
          },
          stacked: false,
          min: props.chart.series[0]?.options?.scale_min,
          max: props.chart.series[0]?.options?.scale_max,
          ticks: isAvgScoreType
            ? {
                callback: (value) => Number(value),
                stepSize: 1,
              }
            : undefined,
          title: {
            display: !!props.yAxisTitle,
            text: props.yAxisTitle || '',
          },
        },
        [isVertical ? 'x' : 'y']: {
          stacked: false,
          ticks: {
            callback(value, index) {
              const labelIndex = Number(value);
              const label =
                visibleLabels?.[labelIndex] ??
                visibleLabels?.[index] ??
                String(value);

              if (typeof label !== 'string') return String(value);
              if (isVertical) return label;

              // y축 label 말줄임: 긴 항목명이 canvas 좌측 경계를 넘어 잘리는 것 방지
              return label.length > MAX_LABEL_LENGTH
                ? label.slice(0, MAX_LABEL_LENGTH) + '\u2026'
                : label;
            },
          },
        },
      },
      plugins: {
        eventPlugin: {
          events: ['mouseleave', 'click'],
        },
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: chartTooltip,
        },
        datalabels: {
          align: 'end',
          anchor: 'end',
          display: true,
          formatter: (value) => {
            return commaNumber(Number(value));
          },
        },
      },
    };
  }, [
    chartTooltip,
    isAvgScoreType,
    isVertical,
    visibleLabels,
    props.chart.series,
  ]);

  const chartNode = (
    <BaseBarChart
      chartRef={chartRef}
      data={visibleAccChartData as ChartData<'bar'>}
      options={barChartOptions}
      plugins={[eventPlugin, ChartDataLabels]}
    >
      {tooltipData && (
        <ChartCustomTooltip
          tooltip={tooltipData}
          meta={props.chart}
          tableData={props.tableData}
          dashboardCardInfo={props.dashboardCardInfo}
        />
      )}
    </BaseBarChart>
  );

  // chart 영역 높이: expanded면 전체 bar, collapsed면 visible bars만
  const chartAreaHeight = expandedChartHeight ?? collapsedChartHeight;

  // Dashboard 수평 bar chart: 안정적 DOM 구조 (CSS로 scroll 제어, Chart.js remount 방지)
  // Non-dashboard: 기존 scroll wrapper 동작 유지
  const innerContent =
    isDashboard && isHorizontal ? (
      <div ref={containerRef} className="flex flex-col w-full flex-1 min-h-0">
        <div
          className={`w-full overflow-x-hidden h-full ${expanded ? 'flex-1 min-h-0' : ''}`}
          style={{ overflowY: expandedChartHeight ? 'auto' : 'hidden' }}
        >
          <div
            className="relative w-full"
            style={{ height: chartAreaHeight ?? '100%' }}
          >
            {chartNode}
          </div>
        </div>
        {hasMore && (
          <div
            className="flex justify-start pt-1"
            style={{ paddingLeft: chartAreaLeft }}
          >
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="text-xs text-gray-500 hover:text-gray-800 hover:underline cursor-pointer bg-transparent border-0 py-0.5"
            >
              {expanded ? 'Collapse' : `Show all ${totalCount} items`}
            </button>
          </div>
        )}
      </div>
    ) : (
      <div className="flex flex-col w-full flex-1 min-h-0">
        <div className="flex-1 min-h-0 flex flex-col">
          {scrollHeight ? (
            <div className="flex-1 w-full overflow-y-auto overflow-x-hidden">
              <div
                style={{ height: '100%', minHeight: scrollHeight }}
                className="relative w-full"
              >
                {chartNode}
              </div>
            </div>
          ) : (
            chartNode
          )}
        </div>
      </div>
    );

  return innerContent;
};

export default BarChart;
